<template>
  <div
    class="activity-landing"
    :style="{ backgroundImage: `url('${backgroundImage}')` }"
  >
    <div v-if="errorMsg" class="activity-landing__error">
      {{ errorMsg }}
    </div>
    <!-- <button
      v-if="actionText"
      class="activity-landing__action"
      @click="handleAction"
    >
      {{ actionText }}
    </button> -->
    <BindAccountDialog
      ref="bindAccountDialog"
      :hash-id="hashId"
      @update="handleAccountBound"
      @close="handleBindClose"
    />
    <ConfirmRoleDialog
      ref="confirmRoleDialog"
      @confirm="handleRoleConfirmed"
      @change-account="handleChangeAccount"
    />
    <ConfirmAccountDialog
      ref="confirmAccountDialog"
      :submit-loading="existingAccountConfirmLoading"
      @confirm="handleExistingAccountConfirm"
      @change-account="handleExistingAccountChange"
    />
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import BindAccountDialog from './BindAccountDialog.vue';
import ConfirmAccountDialog from './ConfirmAccountDialog.vue';
import {
  bindActivityLandingAccount,
  getActivityLandingRoleList,
  getActivityLandingUserInfo,
} from '@/api/activityLanding';
import ConfirmRoleDialog from './ConfirmRoleDialog.vue';
import { getUserInfo as fetchUserInfo } from '@/api/account';
import {
  ACTIVITY_LANDING_BIND_RESULT,
  ACTIVITY_LANDING_BTN_TYPE,
  ACTIVITY_LANDING_POPUP_TYPE,
  reportActivityLandingData,
} from './report';

const DEFAULT_BG = 'https://assets.hzwxbz2.cn/sy/hm/img/pt096.jpg';
const AUTH_FAILED_QUERY = 'auth_failed';

export default {
  name: 'ActivityLanding',
  components: {
    BindAccountDialog,
    ConfirmAccountDialog,
    ConfirmRoleDialog,
  },
  data() {
    return {
      pageConfig: {},
      loading: false,
      errorMsg: '',
      bindDialogOpened: false,
      roleConfirmed: false,
      targetUrl: '',
      pendingRoleData: {},
      existingAccountInfo: {},
      existingAccountConfirmLoading: false,
    };
  },
  computed: {
    ...mapState('account', {
      userInfo: (state) => state.userInfo || {},
    }),
    backgroundImage() {
      return this.pageConfig.background_image || DEFAULT_BG;
    },
    needBindRole() {
      return this.toBool(this.pageConfig.need_bind_role);
    },
    accountName() {
      return this.pageConfig.landing_bind_account || '';
    },
    userId() {
      return this.pageConfig.landing_bind_user_id || '';
    },
    hashId() {
      return this.$route.query.hash_id || '';
    },
    reportActId() {
      return this.pageConfig.act_id || this.$route.query.act_id || '';
    },
    reportActName() {
      return this.pageConfig.act_name || this.$route.query.act_name || '';
    },
    hasRoleInfo() {
      return !!this.pageConfig.role_id;
    },
    actionText() {
      if (this.loading) return '';
      if (!this.accountName) return '绑定账号';
      if (this.needBindRole && !this.roleConfirmed) return '确认角色';
      if (this.targetUrl) return '进入活动';
      return '';
    },
  },
  async mounted() {
    this.setDocumentTitle(this.$route.meta.title || '活动承接页');
    if (this.isAuthFailed()) {
      this.errorMsg = '授权失败，请重新进入活动页面';
      return;
    }
    this.cacheTokenFromQuery();
    await this.initPage();
  },
  watch: {
    '$route.query.hash_id': 'initPage',
  },
  methods: {
    ...mapMutations('account', {
      changeUserInfo: 'CHANGE_USERINFO',
    }),
    // 路由守卫确认授权失败后只展示兜底提示，不再发起无 token 初始化请求。
    isAuthFailed() {
      return this.$route.query[AUTH_FAILED_QUERY] === '1';
    },
    // 初始化承接页配置、缓存通行 token，并按绑定状态进入后续流程。
    async initPage() {
      if (!this.hashId) {
        this.errorMsg = '缺少承接页参数';
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      try {
        const res = await getActivityLandingUserInfo(this.getUserInfoParams());
        this.pageConfig = (res && res.data) || {};
        this.cacheToken(this.pageConfig);
        this.cacheLandingPageToken(this.pageConfig);
        this.syncConfigUserInfo(this.pageConfig);
        this.targetUrl = this.getTargetUrl(this.pageConfig);
        await this.handleLandingInfo();
      } catch (error) {
        this.errorMsg = '承接页配置获取失败，请稍后重试';
      } finally {
        this.loading = false;
      }
    },
    getUserInfoParams() {
      return {
        hash_id: this.hashId,
      };
    },
    // 将承接页返回的用户字段同步到全局 userInfo，保证后续活动页可复用。
    syncConfigUserInfo(config = {}) {
      const userInfo = config.user_info;
      if (userInfo && typeof userInfo === 'object') {
        this.changeUserInfo(userInfo);
        return;
      }
      const plainUserInfo = {
        game_account: config.landing_bind_account || '',
        user_id: config.landing_bind_user_id || '',
        server_id: config.server_id || '',
        server_name: config.server_name || '',
        role_id: config.role_id || '',
        role_name: config.role_name || '',
      };
      const hasUserField = Object.keys(plainUserInfo).some((key) => plainUserInfo[key]);
      if (hasUserField) {
        this.changeUserInfo({
          ...(this.userInfo || {}),
          ...plainUserInfo,
        });
      }
    },
    // 根据承接页绑定状态分流：未绑定走绑定流程，已绑定直接拼参跳转。
    async handleLandingInfo() {
      if (this.pageConfig.landing_page_name) {
        this.setDocumentTitle(this.pageConfig.landing_page_name);
      }
      if (Number(this.pageConfig.is_bound) === 0) {
        await this.handleUnboundLanding();
        return;
      }
      if (Number(this.pageConfig.is_bound) === 1) {
        this.handleBoundAccountLandingRedirect(this.pageConfig);
      }
    },
    // 未绑定时先读取平台账号；已有平台账号则自动绑定，失败或无账号再弹手动绑定框。
    async handleUnboundLanding() {
      try {
        const res = await fetchUserInfo();
        const currentUserInfo = (res && res.data) || {};
        if (currentUserInfo && typeof currentUserInfo === 'object') {
          this.changeUserInfo({
            ...(this.userInfo || {}),
            ...currentUserInfo,
          });
        }
        if (currentUserInfo.game_account) {
          this.openConfirmAccountDialog(currentUserInfo);
          return;
        }
      } catch (error) {
        // 全局用户信息获取失败时不阻塞，回退到手动绑定流程。
      }
      this.openBindAccountDialog();
    },
    // 使用平台已绑定账号直接绑定承接页；接口要求 user_id 为字符串。
    async bindExistingAccount(userInfo = {}) {
      const userId = this.getExistingAccountUserId(userInfo);
      if (!userId) {
        await this.reportConfirmBindResult(ACTIVITY_LANDING_BIND_RESULT.FAIL);
        this.openBindAccountDialog();
        return false;
      }
      try {
        const res = await bindActivityLandingAccount({
          hash_id: this.hashId,
          account: userInfo.game_account || '',
          user_id: userId,
        });
        const result = (res && res.data) || {};
        const landingBindUserId = this.toStringValue(result.landing_bind_user_id || userId);
        await this.reportConfirmBindResult(ACTIVITY_LANDING_BIND_RESULT.SUCCESS);
        this.handleAccountBound({
          ...(res || {}),
          ...result,
          data: result,
          account: result.landing_bind_account || userInfo.game_account || '',
          user_id: landingBindUserId,
          landing_bind_account: result.landing_bind_account || userInfo.game_account || '',
          landing_bind_user_id: landingBindUserId,
          bind_type: 'platform_account',
        });
        return true;
      } catch (error) {
        await this.reportConfirmBindResult(ACTIVITY_LANDING_BIND_RESULT.FAIL);
        this.openBindAccountDialog();
        return false;
      }
    },
    // 保留按钮兜底流程：根据当前账号/角色状态继续绑定、选角或跳转。
    handleIdentityFlow() {
      if (!this.accountName) {
        this.openBindAccountDialog();
        return;
      }
      if (this.needBindRole && !this.roleConfirmed && !this.hasRoleInfo) {
        this.openRoleDialog();
        return;
      }
      this.goTarget();
    },
    openBindAccountDialog() {
      if (this.bindDialogOpened) return;
      this.bindDialogOpened = true;
      this.$nextTick(() => {
        this.$refs.bindAccountDialog && this.$refs.bindAccountDialog.triggerVisible(true);
      });
    },
    openConfirmAccountDialog(userInfo = {}) {
      this.existingAccountInfo = userInfo;
      this.reportConfirmAccountPopupShow();
      this.$nextTick(() => {
        this.$refs.confirmAccountDialog && this.$refs.confirmAccountDialog.open(userInfo);
      });
    },
    // 打开角色弹窗前实时拉取区服角色列表，避免使用旧接口携带的缓存角色数据。
    async openRoleDialog(roleData = {}) {
      const data = {
        ...this.pendingRoleData,
        ...roleData,
      };
      const roleDataWithList = await this.getRoleDialogData(data);
      const roleList = this.getRoleList(roleDataWithList);
      this.$nextTick(() => {
        this.$refs.confirmRoleDialog && this.$refs.confirmRoleDialog.open({
          hash_id: this.hashId,
          user_id: roleDataWithList.landing_bind_user_id || roleDataWithList.user_id || '',
          account: roleDataWithList.landing_bind_account || roleDataWithList.account || '',
          app_key: roleDataWithList.app_key || '',
          act_id: this.reportActId,
          act_name: this.reportActName,
          role_list: roleList,
        });
      });
    },
    // role/list 不缓存角色列表，每次按当前 user_id 实时查询。
    async getRoleDialogData(data = {}) {
      const userId = this.toStringValue(data.landing_bind_user_id || data.user_id || this.userId);
      if (!this.hashId || !userId) {
        return {
          ...data,
          role_list: [],
        };
      }
      try {
        const res = await getActivityLandingRoleList({
          hash_id: this.hashId,
          user_id: userId,
        });
        const result = (res && res.data) || {};
        return {
          ...data,
          ...result,
          data: result,
        };
      } catch (error) {
        return {
          ...data,
          role_list: [],
        };
      }
    },
    // 平台用户信息字段来源不完全固定，统一按优先级提取并转成字符串。
    getExistingAccountUserId(userInfo = {}) {
      return this.toStringValue(userInfo.game_user_id || userInfo.user_id || userInfo.id || '');
    },
    // 账号绑定成功后，同步接口返回的承接页信息，再交给独立跳转流程处理。
    handleAccountBound(data = {}) {
      const shouldBindRole = this.shouldBindRoleAfterAccountBound(data);
      this.bindDialogOpened = false;
      this.pendingRoleData = data;
      this.cacheToken(data);
      this.cacheLandingPageToken(data);
      this.pageConfig = {
        ...this.pageConfig,
        ...data,
      };
      this.syncConfigUserInfo(data);
      this.targetUrl = this.getActivityUrlFromConfig(data) || this.targetUrl;
      this.roleConfirmed = false;
      this.$nextTick(() => {
        this.handleAccountBoundNextStep(data, shouldBindRole);
      });
    },
    // 账号绑定成功后只有两个出口：需要绑定角色则弹角色框，否则直接拼参跳转活动页。
    handleAccountBoundNextStep(data = {}, shouldBindRole = this.shouldBindRoleAfterAccountBound(data)) {
      if (shouldBindRole) {
        this.openRoleDialog(data);
        return;
      }
      this.handleBoundAccountLandingRedirect(data);
    },
    // 已绑定账号进入承接页时，直接拼接公共参数并打开活动链接。
    handleBoundAccountLandingRedirect(data = {}) {
      const activityUrl = this.getActivityUrlFromConfig(data) || this.getTargetUrl(data);
      if (!activityUrl) return;
      this.openLandingActivityUrl(activityUrl, data);
    },
    handleBindClose() {
      this.bindDialogOpened = false;
    },
    async handleExistingAccountConfirm(userInfo = {}) {
      if (this.existingAccountConfirmLoading) return;
      this.existingAccountConfirmLoading = true;
      const accountInfo = Object.keys(userInfo || {}).length ? userInfo : this.existingAccountInfo;
      try {
        await this.bindExistingAccount(accountInfo);
      } finally {
        this.existingAccountConfirmLoading = false;
        this.$refs.confirmAccountDialog && this.$refs.confirmAccountDialog.close();
      }
    },
    handleExistingAccountChange() {
      this.reportChangeAccountClick();
      this.existingAccountInfo = {};
      this.existingAccountConfirmLoading = false;
      this.openBindAccountDialog();
    },
    // 角色绑定完成后同步角色信息，并复用承接页公共参数跳转逻辑。
    handleRoleConfirmed(roleInfo = {}) {
      this.roleConfirmed = true;
      this.cacheToken(roleInfo);
      this.cacheLandingPageToken(roleInfo);
      this.pageConfig = {
        ...this.pageConfig,
        ...roleInfo,
      };
      this.targetUrl = this.getActivityUrlFromConfig(roleInfo) || this.targetUrl;
      this.changeUserInfo({
        ...(this.userInfo || {}),
        game_account: roleInfo.landing_bind_account || '',
        user_id: roleInfo.landing_bind_user_id || '',
        server_id: roleInfo.server_id || '',
        server_name: roleInfo.server_name || '',
        role_id: roleInfo.role_id || '',
        role_name: roleInfo.role_name || '',
      });
      this.handleBoundAccountLandingRedirect(roleInfo);
    },
    handleChangeAccount() {
      this.reportChangeAccountClick();
      this.roleConfirmed = false;
      this.pendingRoleData = {};
      this.bindDialogOpened = false;
      this.openBindAccountDialog();
    },
    // data_type=1：确认账号/确认角色弹窗中的“更换账号”点击上报。
    reportChangeAccountClick() {
      return reportActivityLandingData({
        data_type: 1,
        hash_id: this.hashId,
        btn_type: ACTIVITY_LANDING_BTN_TYPE.CHANGE_ACCOUNT,
      });
    },
    // data_type=2：使用平台已有账号时，第一步确认账号弹窗展示上报。
    reportConfirmAccountPopupShow() {
      return reportActivityLandingData({
        data_type: 2,
        hash_id: this.hashId,
        popup_type: ACTIVITY_LANDING_POPUP_TYPE.CONFIRM_ACCOUNT,
      });
    },
    // data_type=5：使用平台已有账号直接确认绑定的结果上报。
    reportConfirmBindResult(bindResult) {
      return reportActivityLandingData({
        data_type: 5,
        hash_id: this.hashId,
        act_id: this.reportActId,
        act_name: this.reportActName,
        bind_result: bindResult,
        btn_type: ACTIVITY_LANDING_BTN_TYPE.CONFIRM_BIND,
      });
    },
    handleAction() {
      if (!this.accountName) {
        this.openBindAccountDialog();
        return;
      }
      if (this.needBindRole && !this.roleConfirmed && !this.hasRoleInfo) {
        this.openRoleDialog();
        return;
      }
      this.goTarget();
    },
    goTarget(data = {}) {
      const targetUrl = this.getActivityUrlFromConfig(data) || this.targetUrl;
      if (!targetUrl) return;
      this.openLandingActivityUrl(targetUrl, data);
    },
    openLandingActivityUrl(rawUrl, data = {}) {
      const activityUrl = this.appendLandingJumpParams(rawUrl, data);
      if (!activityUrl) return;
      window.location.replace(activityUrl);
    },
    // 给活动链接统一补充承接页公共参数，字段全量透传，空值也保留。
    appendLandingJumpParams(rawUrl, data = {}) {
      const params = this.getLandingJumpParams(data);
      const url = String(rawUrl || '').trim();
      if (!url) return '';
      try {
        const parsedUrl = new URL(url, window.location.origin);
        Object.keys(params).forEach((key) => {
          const value = params[key];
          parsedUrl.searchParams.set(key, value === undefined || value === null ? '' : value);
        });
        return parsedUrl.href;
      } catch (error) {
        return this.appendLandingJumpParamsByString(url, params);
      }
    },
    // URL 构造失败时兜底按字符串拼接，仍然保证公共字段全部存在。
    appendLandingJumpParamsByString(rawUrl, params = {}) {
      const hashIndex = rawUrl.indexOf('#');
      const urlWithoutHash = hashIndex > -1 ? rawUrl.slice(0, hashIndex) : rawUrl;
      const hash = hashIndex > -1 ? rawUrl.slice(hashIndex) : '';
      const query = Object.keys(params).map((key) => {
        const value = params[key];
        return `${encodeURIComponent(key)}=${encodeURIComponent(value === undefined || value === null ? '' : value)}`;
      }).join('&');
      if (!query) return rawUrl;
      return `${urlWithoutHash}${urlWithoutHash.includes('?') ? '&' : '?'}${query}${hash}`;
    },
    // 公共跳转参数只按后端约定的下划线字段取值，没值也保留空参数。
    getLandingJumpParams(data = {}) {
      const source = {
        ...(this.pageConfig || {}),
        ...(data || {}),
      };
      return {
        source: 'hm_activity_landing',
        app_key: source.app_key || '',
        landing_bind_account: source.landing_bind_account || '',
        landing_bind_user_id: source.landing_bind_user_id || '',
        open_id: source.open_id || '',
        platform_user_id: source.platform_user_id || '',
        role_id: source.role_id || '',
        role_name: source.role_name || '',
        server_id: source.server_id || '',
        server_name: source.server_name || '',
        wechat_appid: source.wechat_appid || '',
      };
    },
    getTargetUrl(config = {}) {
      return this.getActivityUrlFromConfig(config);
    },
    // 活动链接只使用后端返回的 activity_url。
    getActivityUrlFromConfig(config = {}) {
      return config.activity_url || '';
    },
    cacheTokenFromQuery() {
      const { access_token: accessToken, token } = this.$route.query;
      if (accessToken || token) {
        localStorage.setItem('access_token', accessToken || token);
      }
    },
    cacheToken(config = {}) {
      const accessToken = config.access_token || config.token;
      if (accessToken) {
        localStorage.setItem('access_token', accessToken);
      }
    },
    cacheLandingPageToken(data = {}) {
      const token = data.landing_page_token;
      if (token) {
        localStorage.setItem('activity_landing_page_token', token);
      }
    },
    getRoleList(data = {}) {
      const roleList = data.role_list || [];
      return Array.isArray(roleList) ? roleList : [];
    },
    // 是否进入角色绑定只看配置/绑定结果标记，角色选项由 role/list 单独获取。
    shouldBindRoleAfterAccountBound(data = {}) {
      return this.toBool(this.pageConfig.need_bind_role)
        || this.toBool(data.need_bind_role);
    },
    toBool(value) {
      return value === true || value === 1 || value === '1' || value === 'true' || value === 'yes';
    },
    toStringValue(value) {
      if (value === undefined || value === null) return '';
      return String(value);
    },
    setDocumentTitle(title) {
      if (!title) return;
      document.title = title;

      // iOS 微信 WebView 异步修改标题后可能不刷新顶部栏，加载隐藏 iframe 强制刷新。
      const isIOS = /ip(hone|od|ad)/i.test(navigator.userAgent);
      if (!isIOS || !document.body) return;
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = '/favicon.ico';
      iframe.onload = () => {
        setTimeout(() => {
          if (iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 0);
      };
      document.body.appendChild(iframe);
    },
  },
};
</script>

<style lang="scss" scoped>
.activity-landing {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background-color: #05070d;
  background-repeat: no-repeat;
  background-position: center top;
  background-size: cover;
}

.activity-landing__error {
  position: absolute;
  right: 16px;
  bottom: 92px;
  left: 16px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #fff;
  background: rgba(0, 0, 0, 0.48);
  font-size: 13px;
  line-height: 20px;
  text-align: center;
}

.activity-landing__action {
  position: fixed;
  right: 24px;
  bottom: calc(24px + env(safe-area-inset-bottom));
  left: 24px;
  height: 42px;
  border: none;
  border-radius: 21px;
  color: #fff;
  background: linear-gradient(90deg, #1677ff 0%, #24a8ff 100%);
  box-shadow: 0 8px 16px rgba(20, 104, 232, 0.25);
  font-size: 15px;
  font-weight: 700;
}
</style>
